/** @jsx isolateComponent */

import { ReactNode } from 'react';

import { Colors, FloatingBox, Typography } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

import { FloatingMenuGetLabelForItem, FloatingMenuItem } from './types';

export type FloatingMenuProps = {
  /**
   * Accessibility label that describes the options available inside the select
   * menu, e.g. "List of items", "List of Countries", etc.
   */
  ariaLabel: string;
  /**
   * Callback that should be passed to the menu root element. Provided by
   * Downshift.
   */
  getMenuProps: (options: { 'aria-label': string }) => any;
  /**
   * Callback that should be passed to each menu item. Provided by Downshift.
   */
  getItemProps: (options: { item: FloatingMenuItem }) => any;
  /**
   * Callback that will be used to generate the react `key` to be used for each
   * item on the menu list.
   */
  getKeyForItem?: (item: FloatingMenuItem) => string;
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: FloatingMenuGetLabelForItem) => ReactNode;
  /**
   * The menu item index that should be highlighted. Provided by Downshift.
   */
  highlightedIndex: number;
  /**
   * The list of items to display in the menu.
   */
  items: Array<FloatingMenuItem>;
  /**
   * The margin between each item in the list.
   */
  marginBetweenItems?: number;
  /**
   * A render prop which receives a menu `item` and the `getLabelForItem`
   * function and returns a React element, which is an icon illustrating the
   * given menu item. The natural size for the icon is 30x30px. If not
   * provided, no icons will be rendered.
   */
  renderIcon?: (
    item: FloatingMenuItem,
    getLabelForItem: FloatingMenuGetLabelForItem
  ) => ReactNode;
};

const Styles = labelStyles(__filename, {
  list: {
    maxHeight: 190,
    overflow: 'auto',
    padding: 10,
  },
  menuItem: (
    isHighlighted: boolean,
    marginBetweenItems: number | undefined
  ) => [
    {
      ...Typography.altText900,
      alignItems: 'center',
      borderRadius: 10,
      cursor: 'pointer',
      display: 'flex',
      marginBottom: marginBetweenItems,
      minHeight: 40,
      padding: '0 10px',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    isHighlighted &&
      withModifier('enabled-highlighted', {
        ...Typography.altText900,
        backgroundColor: Colors.blue100,
        color: Colors.blue500,
        fontWeight: 600,
      }),
  ],
  menuItemIcon: () => [
    {
      marginRight: 10,
    },
  ],
  menuItemText: () => [
    {
      flex: 1,
      overflow: 'hidden' as 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as 'nowrap',
    },
  ],
});

const FloatingMenu = (props: FloatingMenuProps) => {
  const {
    ariaLabel,
    getItemProps,
    getMenuProps,
    highlightedIndex,
    items,
    getKeyForItem,
    getLabelForItem,
    marginBetweenItems,
  } = props;
  return (
    <FloatingBox align="stretch">
      <ul
        css={Styles.list}
        {...getMenuProps({
          'aria-label': ariaLabel,
        })}
      >
        {items.map((item: FloatingMenuItem, index: number) => (
          <li
            {...getItemProps({
              item,
            })}
            css={Styles.menuItem(
              highlightedIndex === index,
              marginBetweenItems
            )}
            key={getKeyForItem ? getKeyForItem(item) : index}
          >
            {props.renderIcon && (
              <span css={Styles.menuItemIcon()}>
                {props.renderIcon(item, props.getLabelForItem)}
              </span>
            )}

            <span css={Styles.menuItemText()}>{getLabelForItem(item)}</span>
          </li>
        ))}
      </ul>
    </FloatingBox>
  );
};

FloatingMenu.defaultProps = {
  marginBetweenItems: 0,
};

export { FloatingMenu };
